import React, { FormEvent, useState, useRef, useEffect } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { UpdatePlanAsync } from '../../../../../services/PlanService/PlanService';
import { useAuth } from '../../../../../firebase/AuthProvider';
import { UpdatePlanRequest, PlanDto } from '../../../../../interfaces';
import { BillingInterval } from '../../../../../Data/SD';

import './plan_createplan.css';

interface EditPlanModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void | Promise<void>;
    plan: PlanDto | null;
}

const EditPlanModal: React.FC<EditPlanModalProps> = ({ isOpen, onClose, onSuccess, plan }) => {
    const { currentUser } = useAuth();
    const [form, setForm] = useState<Partial<UpdatePlanRequest>>({
        name: '',
        basePriceCents: 0,
        seatPriceCents: 0,
        billingInterval: BillingInterval.MONTHLY,
        isActive: true,
    });
    const [errors, setErrors] = useState<Partial<Record<keyof UpdatePlanRequest, string>>>({});
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const controllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        if (plan) {
            setForm({
                name: plan.name,
                basePriceCents: plan.basePriceCents,
                seatPriceCents: plan.seatPriceCents,
                billingInterval: plan.billingInterval as any,
                isActive: plan.isActive,
            });
        }

        return () => {
            controllerRef.current?.abort();
        };
    }, [plan]);

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value, type, checked } = e.target as HTMLInputElement;
        setForm((prev) => ({
            ...prev,
            [name]: type === 'number' ? Number(value) : (type === 'checkbox' ? checked : value),
        } as any));
        setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    const validateForm = () => {
        const newErrors: Partial<Record<keyof UpdatePlanRequest, string>> = {};
        if (!form.name || !form.name.toString().trim()) {
            newErrors.name = 'Please enter a plan name.';
        }
        if (form.basePriceCents === null || form.basePriceCents === undefined || (form.basePriceCents as number) < 0) {
            newErrors.basePriceCents = 'Please enter a base price in cents.';
        }
        if (form.seatPriceCents === null || form.seatPriceCents === undefined || (form.seatPriceCents as number) < 0) {
            newErrors.seatPriceCents = 'Please enter a seat price in cents.';
        }
        if (!form.billingInterval) {
            newErrors.billingInterval = 'Please choose a billing interval.';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!validateForm()) return;
        if (!currentUser || !plan) return;

        const token = await currentUser.getIdToken();
        if (!token) {
            return;
        }

        setIsLoading(true);
        const controller = new AbortController();
        controllerRef.current = controller;

        const payload: UpdatePlanRequest = {
            name: (form.name as string).trim(),
            basePriceCents: form.basePriceCents as number,
            seatPriceCents: form.seatPriceCents as number,
            billingInterval: form.billingInterval as string,
            isActive: Boolean(form.isActive),
        };

        try {
            const res = await UpdatePlanAsync(token, plan.id, payload, controller.signal);
            if (res.success) {
                toast.success('Plan updated successfully');
                onSuccess && onSuccess();
                onClose();
            } else {
                toast.error(res.message);
            }
        } catch (err: any) {
            if (err.name === 'AbortError') {
            } else {
                toast.error(err.message || 'Unable to update plan');
            }
        } finally {
            setIsLoading(false);
            controllerRef.current = null;
        }
    };

    const handleClose = () => {
        controllerRef.current?.abort();
        controllerRef.current = null;
        setErrors({});
        onClose();
    };

    return (
        <Modal
            show={isOpen}
            onHide={handleClose}
            className="custom_popup"
            centered
            backdrop={isLoading ? 'static' : true}
            keyboard={!isLoading}
        >
            <form onSubmit={handleSubmit}>
                <Modal.Header closeButton={!isLoading}>
                    <Modal.Title>Edit Plan</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="plan_formGroup">
                        <label className="plan_label" htmlFor="name">Plan Name</label>
                        <input id="name" name="name" type="text" value={form.name || ''} onChange={handleInputChange} className="plan_input" />
                        {errors.name && <div className="text-red-500 text-sm">{errors.name}</div>}
                    </div>

                    <div className="plan_formGroup">
                        <label className="plan_label" htmlFor="basePriceCents">Base Price (cents)</label>
                        <input id="basePriceCents" name="basePriceCents" type="number" min="0" value={form.basePriceCents ?? 0} onChange={handleInputChange} className="plan_input" />
                        {errors.basePriceCents && <div className="text-red-500 text-sm">{errors.basePriceCents}</div>}
                    </div>

                    <div className="plan_formGroup">
                        <label className="plan_label" htmlFor="seatPriceCents">Seat Price (cents)</label>
                        <input id="seatPriceCents" name="seatPriceCents" type="number" min="0" value={form.seatPriceCents ?? 0} onChange={handleInputChange} className="plan_input" />
                        {errors.seatPriceCents && <div className="text-red-500 text-sm">{errors.seatPriceCents}</div>}
                    </div>

                    <div className="plan_formGroup">
                        <label className="plan_label" htmlFor="billingInterval">Billing Interval</label>
                        <select id="billingInterval" name="billingInterval" value={form.billingInterval as string || BillingInterval.MONTHLY} onChange={handleInputChange} className="plan_select">
                            <option value={BillingInterval.MONTHLY}>Monthly</option>
                            <option value={BillingInterval.ANNUAL}>ANNUAL</option>
                            <option value={BillingInterval.QUATERLY}>QUATERLY</option>
                        </select>
                        {errors.billingInterval && <div className="text-red-500 text-sm">{errors.billingInterval}</div>}
                    </div>

                    <div className="plan_formGroup">
                        <label className="plan_label" htmlFor="isActive">Active</label>
                        <div className="flex items-center gap-2">
                            <input id="isActive" name="isActive" type="checkbox" checked={Boolean(form.isActive)} onChange={handleInputChange} />
                            <span>{form.isActive ? 'Active' : 'Inactive'}</span>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" onClick={handleClose} className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200" disabled={isLoading}>Cancel</button>
                    <Button type="submit" className="px-6 py-2.5 text-sm font-medium rounded-lg bg-[#7E57C2] text-white hover:bg-[#6D4CAB] flex items-center justify-center" disabled={isLoading}>
                        {isLoading ? <Spinner animation="border" size="sm" className="mr-2" /> : 'Update Plan'}
                    </Button>
                </Modal.Footer>
            </form>
        </Modal>
    );
};

export default EditPlanModal;
